import AppShell from "@/components/AppShell";
import { Link } from "react-router-dom";

export function TermsOfService() {
  return (
    <LegalPage title="Terms of Service" testId="legal-tos">
      <p><em>Effective date: February 12, 2026 · Last updated: February 12, 2026</em></p>

      <h2>1. About DS-260USA.com</h2>
      <p>
        DS-260USA.com (&ldquo;DS-260USA,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) provides an
        AI-assisted platform that helps users prepare U.S. immigration paperwork,
        including the U.S. Department of State's Form DS-260 (Immigrant Visa Electronic Application).
        We are <strong>not a law firm</strong> and do not provide legal advice.
        We do not create an attorney-client relationship with you.
      </p>

      <h2>2. Not Legal Advice</h2>
      <p>
        Immigration law is complex. Information on this site and content generated by our
        AI assistants is for general informational purposes only. It is not a substitute for
        the advice of a licensed immigration attorney or accredited representative. You are
        responsible for the accuracy of information you provide and for the final content of
        any forms you submit to USCIS.
      </p>

      <h2>3. Your Account and Access</h2>
      <p>
        The service is currently in private beta and requires an invitation code to register.
        You are responsible for maintaining the confidentiality of your login credentials
        and for all activity that occurs under your account.
      </p>

      <h2>4. Your Content</h2>
      <p>
        You retain all rights to the documents, personal information, and case data you
        upload. You grant DS-260USA.com a limited license to process this content solely
        to provide the service (OCR, translation, form population, PDF generation, and
        related AI features).
      </p>
      <p>
        We use Anthropic&rsquo;s Claude Sonnet 4.5 model (via the Emergent Universal LLM Key)
        to perform OCR and answer paralegal questions. Uploaded documents may be transmitted
        to that provider for processing. We do not sell your data.
      </p>

      <h2>5. Fees</h2>
      <p>
        Some features may require payment of a subscription or per-case fee. USCIS filing
        fees are separate and payable directly to USCIS &mdash; not to DS-260USA.com.
      </p>

      <h2>6. No Warranty</h2>
      <p>
        The service is provided &ldquo;as is,&rdquo; without warranty of any kind. AI-generated
        outputs may contain errors. You must review every form and cover letter carefully
        before submitting anything to USCIS.
      </p>

      <h2>7. Refund Policy</h2>
      <p>
        The <strong>Free Plan</strong> allows prospective customers to evaluate the platform
        before purchase. You can create your case, upload documents, review your data,
        and preview how much of the paperwork the platform prepares for you &mdash; all at
        no cost and with no obligation.
      </p>
      <p>
        Because the <strong>Prepare &amp; File package</strong> consists of immediately usable
        work product, fees are <strong>non-refundable once the completed package has been
        generated and made available to the customer</strong>. Generation of the assembled
        Filing Packet, or the customer&rsquo;s affirmative acceptance of the case as
        &ldquo;Complete and Ready for Filing&rdquo; (case status <code>CASE_ACCEPTED</code>),
        each constitute delivery of the paid work product.
      </p>
      <p>
        DS-260USA.com may, at its <strong>sole discretion</strong>, issue a refund,
        partial refund, or account credit in cases involving:
      </p>
      <ul>
        <li>Duplicate payments;</li>
        <li>Technical failures caused by the platform; or</li>
        <li>Administrative errors on our part.</li>
      </ul>
      <p>
        To request review under this discretionary provision, contact{" "}
        <a href="mailto:support@ds-260usa.com">support@ds-260usa.com</a> with your
        case ID and a description of the issue.
      </p>

      <h2>8. Limitation of Liability</h2>
      <p>
        To the fullest extent permitted by law, DS-260USA.com&rsquo;s aggregate liability
        arising out of or related to your use of the service is limited to the fees you
        paid us in the twelve (12) months preceding the claim. In no event shall we be
        liable for indirect, incidental, consequential, or punitive damages, including
        loss of immigration benefits, denials, or filing rejections.
      </p>

      <h2>9. Termination</h2>
      <p>
        We may suspend or terminate your access at any time for violation of these terms
        or for any other reason. You may terminate your account at any time by contacting us.
      </p>

      <h2>10. Changes</h2>
      <p>
        We may update these terms from time to time. Material changes will be posted here
        and, where reasonable, notified by email.
      </p>

      <h2>11. Contact</h2>
      <p>
        Questions: <a href="mailto:support@ds-260usa.com">support@ds-260usa.com</a>
      </p>

      <div className="mt-8 pt-6 border-t text-xs italic"
           style={{ borderColor: "var(--border-stone)", color: "var(--text-secondary)" }}>
        This document is a template and must be reviewed by qualified legal counsel
        before public production launch.
      </div>
    </LegalPage>
  );
}

export function PrivacyPolicy() {
  return (
    <LegalPage title="Privacy Policy" testId="legal-privacy">
      <p><em>Effective date: February 12, 2026 · Last updated: February 12, 2026</em></p>

      <h2>1. Introduction</h2>
      <p>
        This Privacy Policy explains how DS-260USA.com collects, uses, and protects
        the personal information you provide when using our service.
      </p>

      <h2>2. Information We Collect</h2>
      <ul>
        <li>
          <strong>Account information:</strong> name, email, hashed password, invitation code.
        </li>
        <li>
          <strong>Case information:</strong> data you enter (petitioner and beneficiary
          personal information, addresses, employment history, family history, etc.).
        </li>
        <li>
          <strong>Uploaded documents:</strong> identity documents (passports, birth certificates,
          marriage certificates, and other supporting materials) you upload for OCR processing.
        </li>
        <li>
          <strong>Technical data:</strong> access logs, IP addresses, and error diagnostics for
          security and operations.
        </li>
      </ul>

      <h2>3. How We Use It</h2>
      <ul>
        <li>To perform OCR, translation, and field extraction on uploaded documents.</li>
        <li>To pre-populate immigration forms (DS-260) with the extracted data.</li>
        <li>To generate cover letters, checklists, and filing packets you print and submit yourself.</li>
        <li>To operate, secure, and improve the service.</li>
      </ul>

      <h2>4. Third-Party Processors</h2>
      <p>
        We use Anthropic&rsquo;s Claude Sonnet 4.5 model (via the Emergent Universal LLM Key) for
        OCR, translation, and paralegal chat. Document images and case data may be transmitted
        to this provider strictly for processing. We do not sell your data to anyone.
      </p>

      <h2>5. Retention</h2>
      <p>
        We retain your case data as long as your account is active. You may request deletion
        of your account and associated data at any time by emailing
        {" "}
        <a href="mailto:privacy@ds-260usa.com">privacy@ds-260usa.com</a>.
      </p>

      <h2>6. Security</h2>
      <p>
        We use industry-standard measures including HTTPS transport, bcrypt password hashing,
        and HTTP-only session cookies. No system is 100% secure &mdash; we encourage strong,
        unique passwords and prompt reporting of anything suspicious.
      </p>

      <h2>7. Your Rights</h2>
      <p>
        Depending on your jurisdiction (GDPR, CCPA, other state laws), you may have rights
        to access, correct, delete, port, or restrict processing of your personal data.
        Contact <a href="mailto:privacy@ds-260usa.com">privacy@ds-260usa.com</a> to
        exercise these rights.
      </p>

      <h2>8. Children</h2>
      <p>
        The service is not intended for use by children under 18 as account holders. A parent
        or guardian may enter a child&rsquo;s information as a derivative beneficiary on the case
        of an adult account holder.
      </p>

      <h2>9. Changes</h2>
      <p>Material changes will be posted here and, where reasonable, notified by email.</p>

      <h2>10. Contact</h2>
      <p>
        Privacy inquiries: <a href="mailto:privacy@ds-260usa.com">privacy@ds-260usa.com</a>
      </p>

      <div className="mt-8 pt-6 border-t text-xs italic"
           style={{ borderColor: "var(--border-stone)", color: "var(--text-secondary)" }}>
        This document is a template and must be reviewed by qualified legal counsel
        (with attention to GDPR, CCPA, and state-specific breach-notification laws)
        before public production launch.
      </div>
    </LegalPage>
  );
}

function LegalPage({ title, testId, children }) {
  return (
    <AppShell showNav={false}>
      <div className="mx-auto max-w-3xl px-6 py-14" data-testid={testId}>
        <Link
          to="/"
          className="text-xs font-semibold mb-8 inline-block"
          style={{ color: "var(--action)" }}
        >
          &larr; Back to home
        </Link>
        <h1
          className="font-display text-4xl font-extrabold mb-3"
          style={{ color: "var(--text-primary)" }}
        >
          {title}
        </h1>
        <div className="card-surface p-8 text-sm leading-relaxed prose-legal"
             style={{ color: "var(--text-primary)" }}>
          {children}
        </div>
      </div>
    </AppShell>
  );
}
